import React, { FC, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import Dropdown from 'react-bootstrap/Dropdown';
import { ClaimType, EntityStatus } from '@yakoffice/publisher-types';
import { useAddEntityLink, useGetKindLink } from '../../routes';
import type { IGameEnvironment } from '../../../model/gameEnvironment/GameEnvironment';
import RootStoreContext from '../../../model/RootStoreContext';
import { IGameVersion } from '../../../model/gameVersion/GameVersion';
import { GoogleSheetExistsResult } from '../../../loaders/useGoogleSheetExists';
import { useEditableGameEnvironments } from '../common/useEditableGameEnvironments';
import { equalsIgnoreCase } from '../../../utilities';
import type { ICheckedEntity } from './Entities';
import { EntitiesCsv } from './EntitiesCsv';
import { EntityCompareCopyButtonGroup } from '../entity/EntityCompareCopyButtonGroup';
import { useShowModal } from '@yakoffice/custom-modal';
import { ActionData, FloatingActionButton, FloatingActions } from '@yakoffice/floating-actions';


interface PropsType {
    googleSheetExistsResult: GoogleSheetExistsResult
    checkedEntityVersions: ICheckedEntity[]
    handleBatchCopy: (targetGameVersions: IGameVersion[], targetGameEnvironments: IGameEnvironment[]) => void
    handleBatchStatusUpdate: (arg1: EntityStatus) => void
    handleBatchUpdate: () => void
    handleBatchDelete: () => void
    handleDownloadAsJson: () => void
    handleCreateSheet: () => void
    handleViewSheet: () => void
    handleUploadSheet: () => void
    handleRefreshSheet: () => void
    handleDeleteSheet: () => void
    statusStore: { statusFilters: string[] }
    singleEntityView: boolean,
    isStaticData: boolean
}


export const EntitiesNav: FC<PropsType> = observer((props) => {

    const rootStore = useContext(RootStoreContext);
    const navigate = useNavigate();
    const addEntityLink = useAddEntityLink();
    const editableGameEnvironments = useEditableGameEnvironments();
    const getKindLink = useGetKindLink();
    const showModal = useShowModal();

    // if (props.checkedEntityVersions.length === 1)
    //     runInAction(() => {
    //         props.checkedEntityVersions[0].checked = true;
    //     });

    const disableRequiresCheckedEntitiesButton = props.checkedEntityVersions.filter(e => e.checked).length === 0;
    const disableCopyButton = disableRequiresCheckedEntitiesButton || props.checkedEntityVersions.find(e => e.checked && e.entityVersion.isOnOldVersionOfKind()) != null;
    const showUpdateButton = props.checkedEntityVersions.find(e => e.entityVersion.isOnOldVersionOfKind()) !== undefined;

    const handleAddEntity = () => navigate(addEntityLink);

    const handleCopyEntitiesModal = () => {

        const body = () => <div>

            <p>Select the target game version(s) and game environment(s) for the selected entities.</p>
            <EntityCompareCopyButtonGroup buttonVariant="outline-primary"
                                          gameEnvironments={editableGameEnvironments}
                                          disableCopyButtonOverride={disableCopyButton}
                                          disableCopyToSame={true}
                                          handleCopyEntityVersion={props.handleBatchCopy} />
        </div>;

        showModal(
            {
                show: true,
                title: 'Copy Entities',
                body: body(),
                canClose: true
            }
        );
    };

    const entitiesActions = (): ActionData[] => {

        const actionData: ActionData[] = [{
            icon: 'fas fa-download',
            callback: props.handleDownloadAsJson,
            tooltipText: 'Download',
            subData: [
                <Dropdown.Item eventKey="1"><EntitiesCsv checkedEntities={props.checkedEntityVersions}
                                                         statusStore={props.statusStore} /></Dropdown.Item>,
                <Dropdown.Item eventKey="2" onClick={() => props.handleDownloadAsJson()}><i
                    className="fas fa-file-code"></i> JSON</Dropdown.Item>
            ]
        }];

        if (!props.isStaticData && rootStore.authStore.currentAuthUser.hasGameEnvironmentClaim(ClaimType.EditEntity) && props.checkedEntityVersions.length > 0)
            actionData.push({
                icon: 'fas fa-table',
                tooltipText: 'Google Sheets',
                subData: props.googleSheetExistsResult.spreadSheetExists && props.googleSheetExistsResult.sheetExists ? [
                    <Dropdown.Item as="button" onClick={() => props.handleViewSheet()}>View Sheet</Dropdown.Item>,
                    <Dropdown.Item as="button" onClick={() => props.handleUploadSheet()}>Import from
                        Sheet</Dropdown.Item>,
                    <Dropdown.Item as="button" onClick={() => props.handleRefreshSheet()}>Refresh Sheet</Dropdown.Item>,
                    <Dropdown.Item as="button" onClick={() => props.handleDeleteSheet()}>Delete
                        Spreadsheet</Dropdown.Item>] : [<Dropdown.Item as="button"
                                                                       onClick={() => props.handleCreateSheet()}>Create
                    Google Sheet</Dropdown.Item>]
            });

        if (rootStore.authStore.currentAuthUser.hasProjectClaim(ClaimType.EditKind))
            actionData.push({
                icon: 'fas fa-cog',
                tooltipText: 'Edit configuration',
                href: getKindLink()
            });

        if (rootStore.authStore.currentAuthUser.hasGameEnvironmentClaim(ClaimType.EditEntity) && showUpdateButton &&
            equalsIgnoreCase(rootStore.kindVersionStore.getCurrentKindVersion().kind.status, 'live'))
            actionData.push({
                icon: 'fas fa-file-import',
                callback: props.handleBatchUpdate,
                tooltipText: 'Update'
            });

        if (!disableRequiresCheckedEntitiesButton) {
            if (!props.isStaticData && editableGameEnvironments.length > 0)
                actionData.push({
                    icon: 'fas fa-copy',
                    callback: handleCopyEntitiesModal,
                    tooltipText: 'Copy entities'
                });

            if (!props.isStaticData && rootStore.authStore.currentAuthUser.hasGameEnvironmentClaim(ClaimType.EditEntity) && props.checkedEntityVersions.length > 0)
                actionData.push({
                    icon: 'fas fa-toggle-off',
                    tooltipText: 'Update status',
                    subData: [
                        <Dropdown.Item as="button"
                                       onClick={() => props.handleBatchStatusUpdate(EntityStatus.Published)}>Publish</Dropdown.Item>,
                        <Dropdown.Item as="button"
                                       onClick={() => props.handleBatchStatusUpdate(EntityStatus.Stopped)}>Stop</Dropdown.Item>,
                        <Dropdown.Item as="button"
                                       onClick={() => props.handleBatchStatusUpdate(EntityStatus.Draft)}>Draft</Dropdown.Item>,
                        <Dropdown.Item as="button"
                                       onClick={() => props.handleBatchStatusUpdate(EntityStatus.Archived)}>Archive</Dropdown.Item>
                    ]
                });
        }

        if ((props.isStaticData || rootStore.authStore.currentAuthUser.hasProjectClaim(ClaimType.EditKind)) && !disableRequiresCheckedEntitiesButton)
            actionData.push({
                icon: 'fas fa-trash',
                callback: props.handleBatchDelete,
                tooltipText: 'Delete entities',
                colorClass: 'red'
            });

        return actionData;
    };

    return (
        <>
            <FloatingActions actions={entitiesActions()} position="left" />
            {(rootStore.authStore.currentAuthUser.hasGameEnvironmentClaim(ClaimType.EditEntity) &&
                    equalsIgnoreCase(rootStore.kindVersionStore.getCurrentKindVersion().kind.status, 'live')) &&
                <FloatingActionButton callback={handleAddEntity}
                                      buttonContent={<><i className="fas fa-plus font-light me-2" /> New Entity</>} />
            }
        </>
    );
});
